'use client';

import { useEffect, useState } from 'react';
import {
  DEFAULT_SCHEMA,
  FIELD_TYPE_OPTIONS,
  generateRandomJson,
  type FieldType,
  type SchemaField,
} from '@/lib/browser-mockdata-extra-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

export default function BrowserRandomJsonGenerator() {
  const [fields, setFields] = useState<SchemaField[]>(() => DEFAULT_SCHEMA.map((f) => ({ ...f })));
  const [count, setCount] = useState(5);
  const [output, setOutput] = useState('');
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  function generate() {
    setCopyMessage(null);
    setOutput(generateRandomJson(fields, count));
  }

  useEffect(() => {
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateField(index: number, patch: Partial<SchemaField>) {
    setFields((prev) => prev.map((field, i) => (i === index ? { ...field, ...patch } : field)));
  }

  function addField() {
    setFields((prev) => [...prev, { name: `field_${prev.length + 1}`, type: 'word' }]);
  }

  function removeField(index: number) {
    setFields((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  }

  async function handleCopy() {
    try {
      await copyText(output);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Random JSON</span>
        <h2 className="tool-live-title">Random JSON data generator</h2>
        <p className="tool-live-subtitle">
          Define a field schema, pick how many objects you need, and generate pretty-printed random
          JSON locally in your browser. Nothing is uploaded.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            {fields.map((field, index) => (
              <div className="tool-live-field tool-live-field-full" key={index}>
                <span>Field {index + 1}</span>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <input
                    aria-label={`Field ${index + 1} name`}
                    onChange={(event) => updateField(index, { name: event.target.value })}
                    placeholder="Field name"
                    style={{ flex: '1 1 8rem' }}
                    type="text"
                    value={field.name}
                  />
                  <select
                    aria-label={`Field ${index + 1} type`}
                    onChange={(event) =>
                      updateField(index, { type: event.target.value as FieldType })
                    }
                    style={{ flex: '1 1 8rem' }}
                    value={field.type}
                  >
                    {FIELD_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <button
                    aria-label={`Remove field ${index + 1}`}
                    className="button button-secondary"
                    disabled={fields.length <= 1}
                    onClick={() => removeField(index)}
                    type="button"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
            <label className="tool-live-field">
              <span>How many objects</span>
              <input
                inputMode="numeric"
                onChange={(event) => setCount(Number(event.target.value))}
                type="number"
                value={count}
              />
            </label>
          </div>
          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={addField} type="button">
              Add field
            </button>
            <button className="button button-secondary" onClick={generate} type="button">
              Generate
            </button>
            {output ? (
              <button className="button button-secondary" onClick={handleCopy} type="button">
                Copy result
              </button>
            ) : null}
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <label className="tool-live-field tool-live-field-full">
            <span>Generated JSON</span>
            <textarea readOnly rows={16} value={output} />
          </label>
          <p className="tool-live-panel-note">
            Generated locally in your browser. Click generate for a fresh set (max 1000 objects).
          </p>
        </section>
      </div>
    </div>
  );
}
